import { Check, Clock, FileText, Stethoscope, Activity, MessageCircle, ClipboardList } from "lucide-react";
import { SYMPTOMS, VISIT_INCLUDES } from "@/lib/campaign";
import { Reveal } from "./Reveal";
import { InlineReview } from "./Reviews";
import { CertMarquee } from "./CertMarquee";

export const Recognition = () => (
  <section data-testid="recognition-section" className="py-16 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 lg:gap-16">
      <Reveal>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium text-[var(--brand-navy)] leading-tight">
          When your body feels different, your questions deserve time.
        </h2>
        <p className="mt-6 text-lg leading-relaxed">
          Maybe sleep has become unpredictable. You feel foggy during the day. Or your energy and
          weight have changed, and you&rsquo;re unsure where to start. You don&rsquo;t need to
          arrive knowing whether hormones are the cause. Your visit gives us time to talk through
          what has changed and consider what needs a closer look.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="bg-[var(--brand-cream)] border border-[var(--brand-gold-soft)] p-6 sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[var(--brand-gold)] mb-5">
            Concerns patients often bring
          </p>
          <ul className="space-y-3.5">
            {SYMPTOMS.map((s) => (
              <li key={s} className="flex items-start gap-3 text-base sm:text-lg">
                <Check className="h-5 w-5 mt-0.5 shrink-0 text-[var(--brand-gold)]" aria-hidden="true" />
                {s}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[var(--brand-muted)]">
            These are examples of concerns to discuss, not a diagnostic checklist.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const INCLUDE_ICONS = [MessageCircle, ClipboardList, FileText, Stethoscope, Activity, Clock];

export const VisitDifference = () => (
  <section id="visit" data-testid="visit-difference-section" className="scroll-mt-20 py-16 sm:py-24 bg-white border-y border-[var(--brand-gold-soft)]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <Reveal>
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)] mb-3">
          Why this visit is different
        </p>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium text-[var(--brand-navy)]">
          A full hour to connect the details.
        </h2>
        <p className="mt-5 text-lg leading-relaxed max-w-3xl">
          Sixty minutes gives you room to ask questions, share the context behind what you&rsquo;ve
          been feeling, and leave with a clear understanding of your next step. Your first visit
          includes:
        </p>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--brand-gold-soft)] border border-[var(--brand-gold-soft)]">
        {VISIT_INCLUDES.map((item, i) => {
          const Icon = INCLUDE_ICONS[i % INCLUDE_ICONS.length];
          return (
            <Reveal key={item} delay={i * 0.06} className="bg-white">
              <div className="p-6 h-full">
                <Icon className="h-6 w-6 text-[var(--brand-gold)]" aria-hidden="true" />
                <p className="mt-3 text-base sm:text-lg leading-snug">{item}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <p className="mt-6 text-sm text-[var(--brand-muted)] max-w-3xl">
          Any new testing, treatment, or follow-up visit Dr. Crosson recommends is arranged and
          billed separately.
        </p>
      </Reveal>
    </div>
  </section>
);

export const MeetDoctor = () => (
  <section id="meet" data-testid="meet-doctor-section" className="scroll-mt-20 py-16 sm:py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-center">
      <Reveal>
        <div className="relative mx-auto w-56 sm:w-72">
          <div className="absolute -inset-2.5 rounded-full border border-[var(--brand-gold)] opacity-50" aria-hidden="true" />
          <img
            src="/images/dr-crosson-2.jpg"
            alt="Dr. Jill Crosson, DO, MBA"
            className="relative w-full rounded-full shadow-md"
          />
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium text-[var(--brand-navy)]">
          Meet the physician you&rsquo;ll be seeing.
        </h2>
        <p className="mt-4 text-xl font-serif-brand font-semibold text-[var(--brand-navy)]">
          Dr. Jill Crosson, DO, MBA
        </p>
        <p className="mt-4 text-lg leading-relaxed">
          A board-certified physician with 13 years in practice after fellowship, Dr. Crosson brings
          a background in family medicine and sports medicine, along with additional training in
          functional medicine and hormone health.
        </p>
        <p className="mt-4 text-lg leading-relaxed">
          She starts with your story. Understanding your history, what has changed, and what matters
          to you shapes the conversation about where to go next.
        </p>
        <div className="mt-6 flex items-center gap-4 bg-white border border-[var(--brand-gold-soft)] p-4 max-w-md">
          <img
            src="/images/certs/hormone-badge.png"
            alt="Women's Hormone Therapy Specialist badge, Academy for Advanced Women's Health Medicine"
            className="h-20 w-20 object-contain shrink-0"
          />
          <div>
            <p className="font-semibold text-[var(--brand-navy)]">Women&rsquo;s Hormone Therapy Specialist</p>
            <p className="text-sm text-[var(--brand-muted)]">Academy for Advanced Women&rsquo;s Health Medicine</p>
          </div>
        </div>
        <img src="/images/signature.png" alt="" aria-hidden="true" className="mt-6 h-12 w-auto opacity-80" />
      </Reveal>
    </div>
    <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-12">
      <Reveal>
        <InlineReview
          testId="review-david-vasily"
          quote="Dr. Crosson is a very personable, caring Physician with an amazing depth of knowledge in her field. She practices her specialty with obvious passion for her work and a genuine caring plan personalized for your problem. As a Physician myself, I give her the highest recommendation!"
          name="David Vasily MD"
          sourceLabel="Verified Google Review"
          image="/images/reviews/david-vasily.webp"
        />
      </Reveal>
    </div>
    <CertMarquee />
  </section>
);

export const Expectations = () => (
  <section data-testid="expectations-section" className="py-16 sm:py-24 bg-[var(--brand-navy)] text-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-left sm:text-center">
      <Reveal>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium">
          Start with a conversation, not a predetermined treatment.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-white/85">
          Your visit is an opportunity to discuss your concerns and explore what care may make sense
          for you. If you&rsquo;re interested in bioidentical hormone therapy, bring those
          questions. Dr. Crosson can discuss whether hormone treatment is appropriate based on your
          individual evaluation.
        </p>
      </Reveal>
    </div>
  </section>
);
